import { useTranslation } from "react-i18next";
import useStyles from "./styles";
import { useNavigate, useParams } from "react-router";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Sidebar } from "../../../../containers/Sidebar/Sidebar";
import { CustomTypography } from "../../../../components/CustomTypography/CustomTypography";
import { Icon } from "../../../../components/Icon/Icon";
import { CustomButtonTable } from "../../../../components/CustomButtonTable/CustomButtonTable";
import { CustomTextInput } from "../../../../components/CustomTextInput/CustomTextInput";
import { CustomTextSelect } from "../../../../components/CustomTextSelect/CustomTextSelect";
import { CustomTextDatePicker } from "../../../../components/CustomTextDatePicker/CustomTextDatePicker";
import { CustomTextArea } from "../../../../components/CustomTextArea/CustomTextArea";
import { 
  SelectChangeEvent, 
  Autocomplete, 
  TextField, 
  CircularProgress,
  IconButton,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  LinearProgress
} from "@mui/material";
import { 
  DateValidationError, 
  PickerChangeHandlerContext 
} from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { colors } from "../../../../styles/colors";
import { 
  useGetEnabledEventTypes, 
  useAddEvent, 
  useGetAllEventsWithMileage
} from "../../../../api/event";
import toast, { Toaster } from "react-hot-toast";
import { uploadToStorage } from "../../../../api/media";
import { getCurrentFleetId } from "../../../../utils/getCurrentFleet";
import { searchAddresses as layeredAddressSearch, resolvePlace, MIN_QUERY_LENGTH, AddressSuggestion } from "../../../../services/addressSearch";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";

// Address suggestion type re-exported from the layered service
type BanFeature = AddressSuggestion;

/**
 * Document structure
 */
interface TDoc {
  file: File | null;
  doc_name: string;
  doc_type: string;
  id?: string;
  preview?: string;
  uploadProgress?: number;
  url?: string;
}

/**
 * Event form data structure
 */
interface EventFormData {
  name: string;
  note: string;
  datetime: Dayjs | null;
  eventTypeId: string;
  mileage: string;
  addressId: string;
  addressRaw: string;
  /**
   * Coordonnees du lieu retenu.
   *
   * Elles ne figuraient pas dans le formulaire : seuls `addressRaw` et un
   * `addressId` prefixe ("google-ChIJ…") etaient conserves, et ce dernier etait
   * ignore par l'API. Aucune adresse creee depuis le web n'avait donc de
   * position exploitable.
   */
  addressLat: number | null;
  addressLng: number | null;
  documents: TDoc[];
}

/**
 * Form validation errors structure
 */
type FormErrors = Partial<Record<keyof EventFormData, string>>;

/**
 * Event type option for select dropdown
 */
interface EventTypeOption {
  value: string;
  label: string;
}

/**
 * Mileage validation result
 */
interface MileageValidationResult {
  isValid: boolean;
  error?: string;
  conflictingEvent?: {
    name: string;
    datetime: string;
    mileage: number;
  };
}

/**
 * Layered address search — delegates to services/addressSearch:
 *   1. repertoire interne (sign + address)  2. fournisseur externe, cote serveur
 *
 * Le nom `BanAddressService` est un vestige : ce service n'appelle plus la BAN,
 * ni Google, ni Nominatim depuis le navigateur. Il ne fait plus qu'un appel a
 * notre API, qui decide seule s'il faut solliciter un fournisseur payant.
 */
class BanAddressService {
  private static instance: BanAddressService;
  private constructor() {}

  public static getInstance(): BanAddressService {
    if (!BanAddressService.instance) {
      BanAddressService.instance = new BanAddressService();
    }
    return BanAddressService.instance;
  }

  public async getPlacePredictions(input: string) {
    return layeredAddressSearch(input);
  }

  public isReady(): boolean {
    return true;
  }
}

function debounce<T extends (...args: any[]) => any>(func: T, delay: number): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Validates mileage against chronological event history
 * Handles corrupted data by finding valid ranges even with inconsistent database entries
 */
function validateMileageChronologically(
  newMileage: number,
  newDatetime: Dayjs,
  existingEvents: Array<{ datetime: string; mileage: number; name: string }>
): MileageValidationResult {
  const newTimestamp = newDatetime.valueOf();

  // Separate events into before and after
  const eventsBefore = existingEvents
    .filter(event => new Date(event.datetime).getTime() < newTimestamp)
    .sort((a, b) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime()); // Most recent first

  const eventsAfter = existingEvents
    .filter(event => new Date(event.datetime).getTime() > newTimestamp)
    .sort((a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime()); // Earliest first

  // Find the closest valid previous event (with increasing mileage pattern)
  let previousEvent: typeof existingEvents[0] | null = null;
  for (const event of eventsBefore) {
    if (!previousEvent) {
      previousEvent = event;
    } else if (event.mileage < previousEvent.mileage) {
      // Found a valid chronological sequence
      previousEvent = event;
      break;
    }
  }

  // Find the closest valid next event (with increasing mileage pattern)
  let nextEvent: typeof existingEvents[0] | null = null;
  for (const event of eventsAfter) {
    if (!nextEvent) {
      nextEvent = event;
    } else if (event.mileage > nextEvent.mileage) {
      // Found a valid chronological sequence
      nextEvent = event;
      break;
    }
  }

  // Check if there's a conflict between previous and next constraints
  // This happens when database has corrupted data (e.g., older event with higher mileage than newer event)
  if (previousEvent && nextEvent && previousEvent.mileage >= nextEvent.mileage) {
    // Database has inconsistent data - provide a warning but allow submission
    return {
      isValid: true,
      error: `Attention: Données incohérentes détectées dans l'historique. Assurez-vous que le kilométrage ${newMileage} km est correct pour la date ${newDatetime.format('DD/MM/YYYY')}.`
    };
  }

  // Validate against previous event (mileage must be greater)
  if (previousEvent && newMileage <= previousEvent.mileage) {
    return {
      isValid: false,
      error: `Erreur : Veuillez vérifier le bon kilométrage`,
      conflictingEvent: previousEvent
    };
  }

  // Validate against next event (mileage must be less)
  if (nextEvent && newMileage >= nextEvent.mileage) {
    return {
      isValid: false,
      error: `Erreur : Veuillez vérifier le bon kilométrage`,
      conflictingEvent: nextEvent
    };
  }

  return { isValid: true };
}

export const CreateEvent: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const { id = "" } = useParams<{ id: string }>();
  const vehicleId = parseInt(id, 10);
  const currentFleet = getCurrentFleetId();

  const { eventTypes, isLoading: isLoadingEventTypes } = useGetEnabledEventTypes();
  const addEventMutation = useAddEvent(currentFleet);   

  // Fetch all events with mileage for chronological validation
  const { eventsWithMileage, isLoading: isLoadingMileage } = useGetAllEventsWithMileage(
    currentFleet || 0,
    vehicleId
  );

  const [formData, setFormData] = useState<EventFormData>({
    name: "",
    note: "",
    datetime: null,
    eventTypeId: "",
    mileage: "",
    addressId: "",
    addressRaw: "",
    addressLat: null,
    addressLng: null,
    documents: [],
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [addressSuggestions, setAddressSuggestions] = useState<BanFeature[]>([]);
  /**
   * Credit du fournisseur externe, a afficher SOUS la liste quand il est non nul.
   *
   * Le champ etait deja renvoye par l'API et n'etait lu par personne : les
   * resultats du fournisseur etaient donc affiches sans la mention que ses
   * conditions d'utilisation imposent.
   */
  const [addressAttribution, setAddressAttribution] = useState<string | null>(null);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  /** Vrai pendant l'appel /place/resolve declenche par une selection externe. */
  const [isResolvingAddress, setIsResolvingAddress] = useState(false);
  const [googlePlacesService] = useState(() => BanAddressService.getInstance());
  const [selectedPlace, setSelectedPlace] = useState<BanFeature | null>(null);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  // BAN address service requires no initialization

  useEffect(() => {
    return () => {
      formData.documents.forEach(doc => {
        if (doc.preview) {
          URL.revokeObjectURL(doc.preview);
        }
      });
    };
  }, [formData.documents]);

  // Validate mileage in real-time
  const mileageValidation = useMemo((): MileageValidationResult => {
    if (!formData.mileage || !formData.datetime) {
      return { isValid: true };
    }

    const mileageValue = parseInt(formData.mileage, 10);
    if (isNaN(mileageValue) || mileageValue <= 0) {
      return { isValid: false, error: "Erreur : Veuillez vérifier le bon kilométrage" };
    }

    return validateMileageChronologically(mileageValue, formData.datetime, eventsWithMileage);
  }, [formData.mileage, formData.datetime, eventsWithMileage]);

  // Display warning for inconsistent data but still allow form submission
  const hasDataWarning = mileageValidation.isValid && mileageValidation.error;

  // Check if all required fields are filled
  const areRequiredFieldsFilled = useMemo(() => {
    return !!(
      formData.name.trim() &&
      formData.datetime &&
      formData.eventTypeId &&
      formData.mileage.trim() &&
      /^\d+$/.test(formData.mileage) &&
      mileageValidation.isValid &&
      !hasDataWarning 
    );
  }, [formData.name, formData.datetime, formData.eventTypeId, formData.mileage, mileageValidation.isValid, hasDataWarning]);

  // Check if all documents have been successfully uploaded
  const allDocumentsUploaded = useMemo(() => {
    if (formData.documents.length === 0) return true;
    return formData.documents.every(doc => doc.url && doc.uploadProgress === 100);
  }, [formData.documents]);

  // Button should be blue when all required fields are filled AND all documents are uploaded
  const isFormReadyToSubmit = areRequiredFieldsFilled && allDocumentsUploaded;

  // Button should be disabled when submitting, uploading docs, or required fields not filled
  const isButtonDisabled = isSubmitting || addEventMutation.isPending || isUploadingDocuments || isResolvingAddress || !areRequiredFieldsFilled;

  const performAddressSearch = useCallback(async (searchText: string) => {
    /*
     * Seuil aligne sur le serveur (2 caracteres), et non 3.
     *
     * Le seuil local etait plus haut que celui de l'API : taper « BP » ou le
     * debut d'un code postal ne declenchait aucune recherche, alors que le
     * serveur aurait repondu. Deux seuils pour une meme regle, c'est un des
     * deux qui a tort — ici, celui qui n'a pas la donnee.
     */
    if (!searchText || searchText.trim().length < MIN_QUERY_LENGTH) {
      setAddressSuggestions([]);
      setAddressAttribution(null);
      return;
    }

    setIsLoadingAddresses(true);
    try {
      const result = await googlePlacesService.getPlacePredictions(searchText);
      setAddressSuggestions(result.suggestions);
      setAddressAttribution(result.attribution);
    } catch (error) {
      // address suggestion fetch failed silently
      setAddressSuggestions([]);
      setAddressAttribution(null);
    } finally {
      setIsLoadingAddresses(false);
    }
  }, [googlePlacesService]);

  const searchAddresses = useMemo(
    () => debounce(performAddressSearch, 300),
    [performAddressSearch]
  );

  const handleInputChange = (field: keyof EventFormData, value: string): void => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    if (field === 'addressRaw') {
      searchAddresses(value);
    }
  };

  const handleDateChange = (value: Date | null, _context: PickerChangeHandlerContext<DateValidationError>): void => {
    setFormData(prev => ({ ...prev, datetime: value ? dayjs(value) : null }));
    
    if (errors.datetime) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.datetime;
        return newErrors;
      });
    }
  };

  const handleSelectChange = (field: keyof EventFormData) => (event: SelectChangeEvent<unknown>): void => {
    const selectedOption = event.target.value;
    let value: string;
    
    if (typeof selectedOption === 'string') {
      value = selectedOption;
    } else if (selectedOption && typeof selectedOption === 'object') {
      value = (selectedOption as any).value || '';
    } else {
      value = '';
    }
        
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const getSelectedEventTypeOption = () => {
    return eventTypeOptions.find(option => option.value === formData.eventTypeId) || null;
  };

  /**
   * Selection d'une suggestion.
   *
   * L'ETAPE QUI MANQUAIT : une suggestion externe ne porte qu'un `place_id`,
   * l'autocompletion du fournisseur ne renvoyant aucune geometrie. Sans l'appel
   * de resolution ci-dessous, l'evenement etait enregistre sans latitude ni
   * longitude — silencieusement, puisque `Address::add()` cote API ignore par
   * mass-assignment tout champ hors `raw`/`lat`/`lng`.
   *
   * La resolution fait deux choses d'un coup : elle recupere les coordonnees ET
   * elle enregistre le lieu en base, de sorte que la prochaine recherche du
   * meme garage soit servie par le repertoire interne.
   */
  const handleAddressSelect = async (place: BanFeature | null): Promise<void> => {
    if (!place) {
      setSelectedPlace(null);
      setFormData(prev => ({ ...prev, addressRaw: "", addressId: "", addressLat: null, addressLng: null }));
      return;
    }

    setSelectedPlace(place);
    setFormData(prev => ({
      ...prev,
      addressRaw: place.description,
      addressId: place.address_id != null ? String(place.address_id) : "",
      addressLat: place.lat ?? null,
      addressLng: place.lng ?? null,
    }));

    // Un lieu interne est deja complet : aucun aller-retour.
    if (place.source === "internal" || !place.provider_place_id) return;

    setIsResolvingAddress(true);
    try {
      const resolved = await resolvePlace(place.provider_place_id);

      if (resolved) {
        setSelectedPlace(resolved);
        setFormData(prev => ({
          ...prev,
          addressRaw: resolved.description || prev.addressRaw,
          addressId: resolved.address_id != null ? String(resolved.address_id) : "",
          addressLat: resolved.lat ?? null,
          addressLng: resolved.lng ?? null,
        }));
      } else {
        /*
         * La resolution a echoue : on GARDE le libelle saisi, sans coordonnees.
         *
         * Perdre l'epingle est un incident bien moindre que perdre l'adresse,
         * et l'utilisateur a deja renseigne le reste du formulaire. Mais on le
         * signale, contrairement a la version precedente qui perdait les
         * coordonnees a chaque fois, sans jamais rien dire.
         */
        toast.error(
          t("event.addressResolveFailed", "Adresse conservée, mais sa position n'a pas pu être récupérée")
        );
      }
    } finally {
      setIsResolvingAddress(false);
    }
  };

  const handleDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploadingDocuments(true);

    const newDocuments: TDoc[] = Array.from(files).map(file => {
      const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
      return {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        doc_name: file.name,
        doc_type: 'event_document',
        preview,
        uploadProgress: 0,
      };
    });

    setFormData(prev => ({
      ...prev,
      documents: [...prev.documents, ...newDocuments]
    }));

    // Upload all files in parallel via the media API
    const uploadPromises = newDocuments.map(async (doc) => {
      if (!doc.file) return;
      try {
        const result = await uploadToStorage(doc.file, {
          directory: "strongbox",
          filename: doc.doc_name,
        });
        setFormData(prev => ({
          ...prev,
          documents: prev.documents.map(d =>
            d.id === doc.id ? { ...d, url: result.url, uploadProgress: 100 } : d
          )
        }));
      } catch {
        toast.error(t("event.uploadFailed", "Failed to upload") + ` ${doc.doc_name}`);
        setFormData(prev => ({
          ...prev,
          documents: prev.documents.filter(d => d.id !== doc.id)
        }));
      }
    });

    await Promise.allSettled(uploadPromises);
    setIsUploadingDocuments(false);
    event.target.value = '';
  };

  const handleDocumentRemove = (documentId: string): void => {
    setFormData(prev => {
      const docToRemove = prev.documents.find(doc => doc.id === documentId);
      if (docToRemove?.preview) {
        URL.revokeObjectURL(docToRemove.preview);
      }

      return {
        ...prev,
        documents: prev.documents.filter(doc => doc.id !== documentId)
      };
    });
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t("validation.required", "Ce champ est requis");
      toast.error(t("validation.eventNameRequired", "Event name is required"));
    }

    if (!formData.datetime) {
      newErrors.datetime = t("validation.required", "Ce champ est requis");
      toast.error(t("validation.dateRequired", "Date and time are required"));
    }

    if (!formData.eventTypeId) {
      newErrors.eventTypeId = t("validation.required", "Ce champ est requis");
      toast.error(t("validation.eventTypeRequired", "Event type is required"));
    }

    if (!formData.mileage.trim()) {
      newErrors.mileage = t("validation.required", "Ce champ est requis");
      toast.error(t("validation.mileageRequired", "Mileage is required"));
    } else if (!/^\d+$/.test(formData.mileage)) {
      newErrors.mileage = t("validation.numeric", "Doit être un nombre valide");
      toast.error(t("validation.mileageNumeric", "Mileage must be a valid number"));
    } else if (!mileageValidation.isValid && mileageValidation.error) {
      newErrors.mileage = mileageValidation.error;
      toast.error(mileageValidation.error, { duration: 5000 });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (): Promise<void> => {
    // Use ref for synchronous double-click guard (useState is async)
    if (submittingRef.current || addEventMutation.isPending) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    const hasUploadingDocs = formData.documents.some(doc => !doc.url);
    if (hasUploadingDocs || isUploadingDocuments) {
      toast.error(t("event.waitForUploads", "Please wait for all documents to finish uploading"));
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      const eventData = {
        name: formData.name,
        event_type_id: parseInt(formData.eventTypeId, 10),
        vehicle_id: vehicleId,
        note: formData.note || undefined,
        datetime: formData.datetime?.format('YYYY-MM-DD HH:mm:ss'),
        mileage: formData.mileage,
        ...(formData.addressRaw && {
          address: {
            raw: formData.addressRaw,
            /*
             * `lat` / `lng`, et NON `place_id`.
             *
             * Cote API, `Address::add()` fait un `fill()` sur un modele dont le
             * `$fillable` ne declare que raw / lat / lng. `place_id` etait donc
             * ecarte en silence par la protection contre le mass-assignment :
             * la cle partait sur le reseau, l'API la recevait, et personne ne
             * l'ecrivait nulle part.
             *
             * `?? undefined` plutot que `null` : une adresse saisie a la main
             * n'a pas de position, et envoyer `null` ecraserait la colonne
             * plutot que de la laisser vide.
             */
            lat: formData.addressLat ?? undefined,
            lng: formData.addressLng ?? undefined,
          }
        }),
        documents: formData.documents
          .filter((doc): doc is TDoc & { url: string } => !!doc.url)
          .map(doc => ({
            url: doc.url,
            doc_name: doc.doc_name,
            doc_type: doc.doc_type
          }))
      };

      await addEventMutation.mutateAsync(eventData);
      toast.success(t("event.createSuccess", "Event created successfully"));
      navigate(`/vehicule/update/${vehicleId}`, { replace: true });
    } catch (error) {
      toast.error(t("event.createError", "Please check the date"));
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };


  const handleCancel = (): void => {
    navigate(`/vehicule/update/${vehicleId}`);
  };

  const eventTypeOptions: EventTypeOption[] = eventTypes.map(type => ({
    value: type.id.toString(),
    label: type.libelle
  }));

  return (
    <>
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: colors.primary,
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: colors.error,
              secondary: '#fff',
            },
          },
        }}
      />
      <Sidebar
        styles={{
          sidebarRightRoot: classes.eventRoot,
          sidebarRoot: classes.contentMain,
        }}
      >
        <div className={classes.container}>
          <div className={classes.containerHeader}>
            <div>
              <CustomTypography
                variant="h2"
                text={t("event.createEvent", "Create Event")}
                lineHeight="47px"
              />
              <div className={classes.headerIconContainer}>
                <Icon name="vehicule" height={24} width={24} />
                <CustomTypography 
                  variant="default" 
                  text={`${t("vehicule.vehicule", "Vehicle")} ID: ${vehicleId}`} 
                />
              </div>
            </div>

            <div className={classes.buttonContainer}>
              <CustomButtonTable
                variant="secondary"
                textVariant="link"
                label={t("common.cancel", "CANCEL")}
                width="fit-content"
                fontWeight="bold"
                textDecoration="none"
                onClick={handleCancel}
              />
              <CustomButtonTable
                variant={isFormReadyToSubmit ? "primary" : "default"}
                textVariant="link"
                label={t("event.createEvent", "CREATE EVENT")}
                width="fit-content"
                fontWeight="bold"
                textDecoration="none"
                onClick={handleSubmit}
                iconName="check"
                disabled={isButtonDisabled}
                styles={{
                  backgroundColor: isFormReadyToSubmit ? colors.primary : undefined
                }}
              />
            </div>
          </div>

          <div className={classes.formContainer}>
            <div className={classes.formCard}>
              <div className={classes.sectionTitle}>
                <CustomTypography
                  variant="h3"
                  text={t("event.eventDetails", "Event Details")}
                />
              </div>

              <div className={classes.cardContent}>
                <div className={classes.formRow}>
                  <div className={classes.formField}>
                    <CustomTextInput
                      label={t("event.eventName", "Event Name")}
                      value={formData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      placeholder={t("event.eventNamePlaceholder", "Enter event name")}
                    />
                    {errors.name && (
                      <CustomTypography text={errors.name} variant="default" color={colors.error} />
                    )}
                  </div>
                  
                  <div className={classes.formField}>
                    <CustomTextSelect
                      label={t("event.eventType", "Event Type")}
                      value={getSelectedEventTypeOption()}
                      onChange={handleSelectChange("eventTypeId")}
                      options={eventTypeOptions}
                      placeholder={t("event.selectEventType", "Select event type")}
                      disabled={isLoadingEventTypes}
                    />
                    {isLoadingEventTypes && <CircularProgress size={20} />}
                    {errors.eventTypeId && (
                      <CustomTypography text={errors.eventTypeId} variant="default" color={colors.error} />
                    )}
                  </div>
                </div>

                <div className={classes.formRow}>
                  <div className={classes.formField}>
                    <CustomTextDatePicker
                      label={t("event.dateTime", "Date & Time")}
                      value={formData.datetime?.toDate() || null}
                      onChange={handleDateChange}
                    />
                    {errors.datetime && (
                      <CustomTypography text={errors.datetime} variant="default" color={colors.error} />
                    )}
                  </div>
                  
                  <div className={classes.formField}>
                    <CustomTextInput
                      label={t("event.mileage", "Mileage")}
                      value={formData.mileage}
                      onChange={(e) => handleInputChange("mileage", e.target.value)}
                      placeholder={t("event.mileagePlaceholder", "Enter current mileage")}
                      type="number"
                    />
                    {isLoadingMileage && (
                      <CustomTypography 
                        text={t("event.loadingMileage", "Loading current mileage...")} 
                        variant="default" 
                        color={colors.primary} 
                      />
                    )}
                    {hasDataWarning && mileageValidation.error && (
                      <Box sx={{ 
                        mt: 1, 
                        p: 1, 
                        bgcolor: '#fff3cd', 
                        borderLeft: '4px solid #ffc107',
                        borderRadius: '4px'
                      }}>
                        <CustomTypography 
                          text={mileageValidation.error} 
                          variant="default" 
                          color="#856404"
                          styles={{ fontSize: '0.875rem' }}
                        />
                      </Box>
                    )}
                    {!mileageValidation.isValid && mileageValidation.error && (
                      <CustomTypography 
                        text={mileageValidation.error} 
                        variant="default" 
                        color={colors.error} 
                      />
                    )}
                    {errors.mileage && (
                      <CustomTypography text={errors.mileage} variant="default" color={colors.error} />
                    )}
                  </div>
                </div>

                <div className={classes.formRow}>
                  <div className={classes.formField}>
                    <Autocomplete
                      options={addressSuggestions}
                      getOptionLabel={(option) => option.description}
                      groupBy={(option) =>
                        option.source === "internal"
                          ? t("event.knownGarages", "Vos garages & adresses")
                          : t("event.externalSuggestions", "Suggestions")
                      }
                      renderOption={(props, option) => (
                        <li {...props} key={option.place_id}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 30,
                                height: 30,
                                borderRadius: 8,
                                flexShrink: 0,
                                backgroundColor:
                                  option.source === 'internal'
                                    ? 'rgba(34,197,94,0.12)'
                                    : 'rgba(37,99,235,0.10)',
                                fontSize: 15,
                              }}
                            >
                              {option.source === 'internal' ? '🔧' : '📍'}
                            </span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {option.structured_formatting.main_text}
                              </div>
                              <div style={{ fontSize: '0.8em', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {option.structured_formatting.secondary_text}
                              </div>
                            </div>
                            {/*
                              * Distance, a la place du badge « N× ».
                              *
                              * L'ancien badge lisait `usage_count`, un champ de
                              * l'endpoint que PlaceSearch a remplace : il etait
                              * toujours undefined, donc jamais rendu. La
                              * distance, elle, est reellement renvoyee des que
                              * la position de l'appelant est transmise, et
                              * repond a la question que l'utilisateur se pose
                              * devant trois garages du meme nom.
                              */}
                            {typeof option.distance_km === 'number' ? (
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: '#16A34A',
                                  backgroundColor: 'rgba(34,197,94,0.10)',
                                  border: '1px solid rgba(34,197,94,0.25)',
                                  borderRadius: 999,
                                  padding: '2px 8px',
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0,
                                }}
                              >
                                {option.distance_km} km
                              </span>
                            ) : null}
                          </div>
                        </li>
                      )}
                      loading={isLoadingAddresses}
                      value={selectedPlace}
                      onChange={(_, newValue) => handleAddressSelect(newValue)}
                      inputValue={formData.addressRaw}
                      onInputChange={(_, newInputValue) => {
                        handleInputChange("addressRaw", newInputValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("event.location", "Location") as string}
                          placeholder={t("event.searchGarageOrAddress", "Rechercher un garage ou une adresse") as string}
                          InputLabelProps={{
                            ...params.InputLabelProps,
                            children: undefined,
                          }}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start" sx={{ ml: 0.5 }}>
                                <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <>
                                {(isLoadingAddresses || isResolvingAddress) ? <CircularProgress size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      noOptionsText={t("event.noLocationFound", "No locations found")}
                      style={{ width: '100%' }}
                    />
                    {/*
                      * Credit du fournisseur externe.
                      *
                      * Ses conditions d'utilisation l'imposent des que ses
                      * resultats sont affiches — c'est-a-dire exactement quand
                      * le serveur renvoie une `attribution` non nulle. Rendu
                      * verbatim, jamais traduit, et jamais affiche pour un
                      * resultat interne.
                      */}
                    {addressAttribution && (
                      <Box sx={{ mt: 0.5, textAlign: 'right' }}>
                        <CustomTypography
                          variant="default"
                          text={addressAttribution}
                          color={colors.mutedText}
                          styles={{ fontSize: '0.75rem' }}
                        />
                      </Box>
                    )}
                  </div>
                </div>

                <div className={classes.formRow}>
                  <div className={classes.formFieldFull}>
                    <Box sx={{ mb: 2 }}>
                      <CustomTypography
                        variant="default"
                        text={t("event.documents", "Documents")}
                        styles={{ marginBottom: '8px', fontWeight: 500 }}
                      />
                      <input
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                        style={{ display: 'none' }}
                        id="document-upload"
                        multiple
                        type="file"
                        onChange={handleDocumentUpload}
                        disabled={isUploadingDocuments}
                      />
                      <label htmlFor="document-upload">
                        <CustomButtonTable
                          variant="secondary"
                          textVariant="link"
                          label={t("event.uploadDocuments", "UPLOAD DOCUMENTS")}
                          width="fit-content"
                          fontWeight="bold"
                          textDecoration="none"
                          onClick={() => document.getElementById('document-upload')?.click()}
                          iconName="upload"
                          disabled={isUploadingDocuments}
                        />
                      </label>
                      {isUploadingDocuments && (
                        <Box sx={{ mt: 1 }}>
                          <CustomTypography
                            variant="default"
                            text={t("event.uploadingDocuments", "Uploading documents...")}
                            color={colors.primary}
                          />
                        </Box>
                      )}
                    </Box>

                    {formData.documents.length > 0 && (
                      <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                        {formData.documents.map((doc) => (
                          <ListItem
                            key={doc.id}
                            sx={{
                              border: '1px solid #1E293B',
                              borderRadius: 1,
                              mb: 1,
                              '&:hover': { bgcolor: '#243044' }
                            }}
                          >
                            {doc.preview && (
                              <Box
                                component="img"
                                src={doc.preview}
                                alt={doc.doc_name}
                                sx={{
                                  width: 50,
                                  height: 50,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                  mr: 2
                                }}
                              />
                            )}
                            <ListItemText
                              primary={doc.doc_name}
                              secondary={
                                doc.file 
                                  ? formatFileSize(doc.file.size) 
                                  : doc.url 
                                  ? t("event.uploaded", "Uploaded") 
                                  : t("event.uploading", "Uploading...")
                              }
                            />
                            {!doc.url && (
                              <Box sx={{ width: '100px', mr: 2 }}>
                                <LinearProgress />
                              </Box>
                            )}
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                aria-label="delete"
                                onClick={() => handleDocumentRemove(doc.id!)}
                                sx={{ color: colors.error }}
                              >
                                <span style={{ fontSize: '20px' }}>×</span>
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </div>
                </div>

                <div className={classes.formRow}>
                  <div className={classes.formFieldFull}>
                    <CustomTextArea
                      label={t("event.notes", "Notes")}
                      value={formData.note}
                      onChange={(value) => handleInputChange("note", value)}
                      placeholder={t("event.notesPlaceholder", "Enter additional notes or details about this event")}
                      rows={4}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Sidebar>
    </>
  );
};